import { UserIdentityValidationResultOutputDto } from '../../../dto';
import {
  DepositStep,
  KycCheckpointId,
  KycIdentityOriginId,
  UserIdentityStatus,
} from '../../../enums';
import { UserIdentity } from '../../../models';
import { useUserOriginId } from '../../user-identity/useUserOriginId';
import { usePhoneNumberForm } from '../../user-phone-number/useUserPhoneNumberForm';
import { useDepositForm } from '../useDepositForm';
import useDepositSearchParams from '../useDepositSearchParams';
import { UseDepositFormStateProps } from './props';

/**
 * Custom hook to manage the state of a deposit form.
 * This hook determines the validity of the form based on various parameters and conditions.
 *
 * @param {UseDepositFormStateProps} props - The properties required for the hook.
 * @param {Function} props.watch - Function to watch form fields.
 * @param {Function} props.setValue - Function to set form field values.
 * @returns {boolean} - Returns a boolean indicating if the form is valid or not.
 *
 * @example
 * // Usage example:
 * const isFormValid = useDepositFormState({
 *   watch: formWatchFunction,
 *   setValue: formSetValueFunction,
 * });
 */
export function useDepositFormState({
  watch,
  setValue,
}: UseDepositFormStateProps) {
  // Extract deposit method ID and transaction step from search parameters
  const { depositMethodId, transactionStep } = useDepositSearchParams();
  // Get the user's origin ID
  const originId = useUserOriginId();

  // Watch specific form fields
  const amount = watch('amount');
  const identifier: string | undefined = watch('identity.identifier');
  const name = watch('identity.name');
  const birthDate = watch('identity.birthDate');

  // Destructure values from the useDepositForm hook
  const {
    isOverValued,
    isUnderValued,
    useKycMeByOriginIdQuery: { kyc },
    isValidReceiveAmount,
    personalIdentityLookUpQuery: {
      personalIdentityLookup,
      errorPersonalIdentityLookup,
    },
  } = useDepositForm(watch, setValue);

  // Destructure values from the usePhoneNumberForm hook
  const { isSubmitDisabled } = usePhoneNumberForm({ watch, setValue });

  // Determine form validity based on the current transaction step
  switch (transactionStep) {
    case DepositStep.Form:
      // Check conditions for the Form step
      return (
        !amount ||
        amount === 0 ||
        !depositMethodId ||
        isOverValued ||
        isUnderValued ||
        !isValidReceiveAmount
      );

    case DepositStep.CreatePersonalIdentity:
      // Check conditions based on the user's origin ID
      switch (originId?.toString()) {
        case KycIdentityOriginId.Brazil?.toString():
          // Conditions specific to Brazil
          return (
            !identifier ||
            (personalIdentityLookup as unknown as UserIdentity)?.status ==
              UserIdentityStatus.Invalid ||
            !!errorPersonalIdentityLookup
          );
        case KycIdentityOriginId.India?.toString():
        case KycIdentityOriginId.Mexico?.toString():
        case KycIdentityOriginId.Argentina?.toString():
          // Conditions for India, Mexico, and Argentina
          switch (kyc?.stage?.checkpoint?.id?.toString()) {
            case KycCheckpointId.VerifiedPhoneNumber?.toString():
              return isSubmitDisabled;
            case KycCheckpointId.Identity?.toString():
            default:
              return (
                !identifier ||
                !name ||
                !birthDate ||
                (!!personalIdentityLookup &&
                  !(
                    personalIdentityLookup as UserIdentityValidationResultOutputDto
                  )?.valid)
              );
          }
      }
      break;
    case DepositStep.Response:
      // Always return false for the Response step
      return false;
  }
}

export type { UseDepositFormStateProps };
